import {
    Component,
    ChangeDetectionStrategy,
    input,
    ViewEncapsulation,
    forwardRef,
    Inject,
    Optional,
    SkipSelf,
    signal,
    viewChild,
    computed,
    effect,
    Signal
} from "@angular/core";
import { InputFormWrapper, VContainerComponent } from "../wrapper.interface";
import { ControlContainer, NG_VALUE_ACCESSOR, ReactiveFormsModule } from "@angular/forms";
import { CommonModule } from "@angular/common";
import { V_COMPONENT_CONTAINER, V_FORM_CONTROL_TOKEN, WRAPPER_TOKEN } from "../wrapper.tokens";
import { TagConfig } from "./tag.config";
import { VAutoCompleteComponent } from "../autoComplete/autoComplete.component";
import { ITag, TagType } from "@vierkant-software/types__api";
import { isSet } from "src/util/string";
import { TagService } from "src/app/services/tag.service";
import { TranslateModule } from "@ngx-translate/core";
import { signalMarkAsDirty } from "src/app/util/signal";

@Component({
    selector:    'v-tag',
    templateUrl: './tag.component.haml',
    styleUrls:   ['./tag.component.sass', '../input.style.sass'],
    providers:   [
        { provide: V_FORM_CONTROL_TOKEN, useExisting: forwardRef(() => VTagComponent) },
        { provide: WRAPPER_TOKEN, useExisting: forwardRef(() => VTagComponent) },
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => VTagComponent), multi: true }
    ],
    encapsulation:   ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports:         [CommonModule, VAutoCompleteComponent, ReactiveFormsModule, TranslateModule]
})
export class VTagComponent extends InputFormWrapper<TagConfig, string[]> {
    protected _baseConfig: TagConfig = TagConfig.baseTheme.Default;
    public tagType = input.required<TagType>({alias: 'type'});
    private internalValue = signal<string[]>([]);
    private values = computed(() => {
        const ids = this.internalValue();
        const tags = this.tags().filter(x => ids.includes(x.ID));
        return tags;
    });
    private searchTerm = signal<string>(undefined);
    /**
     * Suggestions for the autoComplete component
     */
    public suggestions = computed<ITag[]>(() => {
        const tags = this.tags();
        const term = this.searchTerm();
        const query = term?.toLocaleLowerCase();

        const selectedValues = this.values().map(x => x.value.toLocaleLowerCase());
        const filtered = tags.filter(x => {
            const value = x.value.toLocaleLowerCase();
            return value.includes(query) && !selectedValues.includes(value);
        });

        if (!isSet(query) || filtered.find(x => x.value.toLowerCase() === query) || !this.tagService.hasCreatePermission)
            return filtered; // no create
        return [{value: term},...filtered]; // create new tag with `term`

    });
    /**
     * All tags of a given `TagType`
     */
    public tags = computed(() => {
        const type = this.tagType();
        const tags = this.getTags(type);
        return tags;
    });

    /**
    * Reference to autoComplete component
    */
    private autoComplete = viewChild.required<VAutoCompleteComponent<ITag>>("autoComplete");
    private controlValue: Signal<ITag[]>;

    constructor(
        @Optional() protected controlContainer: ControlContainer,
        protected tagService: TagService,
        @Inject(V_COMPONENT_CONTAINER) @Optional() @SkipSelf() protected parent?: VContainerComponent,
    ) {
        super(controlContainer, parent);
        effect(() => { // Restore tags on draft restore after they are fetched from the backend
            this.autoComplete().writeValue(this.tags().filter(x => this.value.includes(x.ID)));
        });
        effect(() => {
            /**
             * re-set value when control changes (internalValue is a string representation, the actual component holds tags)
             * FIXME better way to handle this would be to internally rewrite `_value` to be a signal, but that requires too many changes for now.
             */
            this.internalValue.set(this.cControl().value);
        });
    }


    getTags(type: TagType) {
        return this.tagService.getTags(type);
    }

    search = (term: string) => {
        this.searchTerm.set(term = term.trim());
    };

    /**
    * @param event 
    * @returns 
    */
    public async addTags(tags: ITag[]) {
        if (!this.tagService.hasCreatePermission) return;
        const newTags = tags.filter(x => !x.ID && x.value);
        await Promise.all(newTags.map(async x => {
            const tag = await this.tagService.createTag({type: this.tagType(), value: x.value});
            Object.assign(x, tag ?? {ID: undefined});
        }));
    }

    private hasInput: boolean;
    public async onKeyUp(event: KeyboardEvent) {
        if (event.key !== "Enter")this.hasInput = true;
        if (event.key !== "Enter" || !this.hasInput || !isSet(this.searchTerm())) return;

        const searchTerm = this.searchTerm().toLocaleLowerCase();
        const suggestion = this.suggestions().find(x => x.value.toLocaleLowerCase() === searchTerm);

        if (!suggestion) return;
        if (!suggestion.ID) {
            const tag = await this.tagService.createTag({type: this.tagType(), value: suggestion.value});
            Object.assign(suggestion, tag ?? {ID: undefined});
        }
        this.value.push(suggestion.ID);
        this.internalValue.set([...this.value]); // workaround to trigger change detection FIXME

        this.autoComplete().writeValue(this.values());
        this.writeValue(this.value);

        this.searchTerm.set(undefined);
        this.autoComplete().clearInput();
    }

    public async onChanged([_oldTags, newTags]: [ITag[], ITag[]]) {
        if (!this.tagService.fetched()) return;
        this.hasInput = false;
        await this.addTags(newTags);
        newTags = newTags.filter(x => !!x.ID);
        this.writeValue(newTags.map(x => x.ID));
        this.autoComplete().control.setValue(newTags, {emitEvent: false});
        signalMarkAsDirty(this.internalValue);
    }

    public onShow(event: unknown) {
        /**
         * Changes to the component value prompt for a new suggestions list.
         * Suggestions must be a new array ref for the autoComplete component to update.
         * Otherwise the suggestions list will not be shown and a spinner will be shown.
         */
        signalMarkAsDirty(this.searchTerm);
    }
}